import { useEffect, useRef, useState } from 'react';
import {
  Index,
  useHits,
  useMenu,
  useRefinementList,
  useSearchBox,
} from 'react-instantsearch';
import { useRouter } from 'next/compat/router';
import Link from 'next/link';
import { Popover } from '@headlessui/react';
import { oneLine } from 'common-tags';

import ChevronDown from '@/icons/light/chevron-down.svg';
import ChevronUp from '@/icons/light/chevron-up.svg';
import HomFilters from '@/icons/light/hom-filters.svg';
import HomMapPin from '@/icons/light/hom-map-pin.svg';
import MapPin from '@/icons/light/map-pin.svg';

import { useAlgoliaDebounce } from '@/hooks';
import { routes } from '@/lib';
import { formatNumberToCurrency } from '@/utils';
import { priceFilterOptions, sortZonasResults, zonePath } from '../utils';

import type { TypeHitZone } from '@/types';
import type { TypeFlat } from '@/types/analytics/flat';
import type { SearchTypeRoutes } from '@/types/searchTypeRoutes';
import type { FunctionComponent, MouseEvent } from 'react';
import type { TypeSearchBoxHeroProps } from './types';

// `typeFlat` viene del enum Strapi `InstantSearchSettings.TipoVivienda`
// (`ALQUILER` | `MED_EST` | …) — ya es el valor de `ventaAlquiler` del índice.
// Para navegar al listado necesitamos el slug de ruta correspondiente
// (`rent` | `sale` | `half`), por eso este mapeo inverso. El env var
// `NEXT_PUBLIC_MAIN_SEARCH_TYPE` (usado como fallback) sí está en formato
// `SearchTypeRoutes`, así que también lo traducimos a `ventaAlquiler`.
const VENTA_TO_ROUTE: Record<TypeFlat['ventaAlquiler'], SearchTypeRoutes> = {
  ALQUILER: 'rent',
  VENTA: 'sale',
  MED_EST: 'half',
  ALQ_VENT: 'half',
};
const ROUTE_TO_VENTA: Record<SearchTypeRoutes, TypeFlat['ventaAlquiler']> = {
  rent: 'ALQUILER',
  sale: 'VENTA',
  half: 'MED_EST',
};

// Vivienda y Habitaciones SÍ son facets del índice de flats y se piden vía
// `useMenu` dentro de un `<Index>`, así no hay que mantener listas a mano:
// los valores y los conteos vienen directos de Algolia y crecen solos.
const FLATS_INDEX_NAME =
  process.env.NEXT_PUBLIC_ALGOLIA_SEARCH_FLATS_INDEX_NAME ?? '';

/**
 * Render del nombre de una zona en el dropdown de sugerencias. Algolia
 * guarda los nombres con casing inconsistente (`Madrid > MADRID > Cañaveral`);
 * lo normalizamos a "Capitalize per segment" y sustituimos el `>` por `›`.
 * La parte que matchea con la query del usuario va en negrita + color de
 * texto fuerte; el resto queda en gris para que la match destaque sin
 * ruido visual (no fondos ni colores raros).
 */
const HighlightHitName: FunctionComponent<{
  name: string;
  query: string;
}> = ({ name, query }) => {
  const segments = (name ?? '').split(' > ');
  const q = query.trim().toLowerCase();

  return (
    <span className="text-text-neutrals-500">
      {segments.map((segment, index) => {
        const lower = segment.toLowerCase();
        const display = lower.charAt(0).toUpperCase() + lower.slice(1);
        const matchIdx = q ? lower.indexOf(q) : -1;
        const matchEnd = matchIdx + q.length;
        return (
          <span key={index}>
            {index > 0 && (
              <span aria-hidden className="text-text-neutrals-300 mx-1">
                ›
              </span>
            )}
            {matchIdx === -1 ? (
              display
            ) : (
              <>
                {display.slice(0, matchIdx)}
                <strong className="text-text-neutrals-900 font-semibold">
                  {display.slice(matchIdx, matchEnd)}
                </strong>
                {display.slice(matchEnd)}
              </>
            )}
          </span>
        );
      })}
    </span>
  );
};

// Mismo `Intl.NumberFormat('es-ES', currency: EUR)` que usa `CustomSelect` en
// el listado (`formatNumberToCurrency`) → "150.000 €" / "1.000 €/mes",
// sin abreviaturas tipo "k". Un único formato para hero y listado.
const formatPrice = (value: number, isRent: boolean) => {
  const formatted = formatNumberToCurrency(value, 2, 2);
  return isRent ? `${formatted}/mes` : formatted;
};

type DropdownOption = { value: string; label: string };

/**
 * Dropdown ligero con estado local (no Algolia) — para Vivienda / Precio /
 * Habitaciones, cuyas opciones no viven en el índice de zonas al que está
 * conectado el hero. El valor seleccionado viaja como query param al listado.
 */
const LocalDropdown: FunctionComponent<{
  label: string;
  options: DropdownOption[];
  selected?: string;
  onSelect: (value: string | undefined) => void;
  clearLabel?: string;
  /**
   * Ancho del dropdown. Default `min-w-[180px] flex-1`: estira a partes iguales
   * en su fila. La fila oculta de filtros pasa un ancho fijo para no estirarse
   * y quedar alineada con la primera fila (Figma).
   */
  sizeClass?: string;
}> = ({
  label,
  options,
  selected,
  onSelect,
  clearLabel = 'Cualquiera',
  sizeClass = 'min-w-[180px] flex-1',
}) => (
  <Popover
    as="div"
    className={oneLine`text-body text-body-s relative ${sizeClass}`}
  >
    {({ open, close }) => {
      const Icon = open ? ChevronUp : ChevronDown;
      return (
        <>
          <Popover.Button
            className={oneLine`border-input-border bg-input-bg text-neutrals-0 hover:bg-input-bg/90 inline-flex w-full items-center justify-between gap-2 rounded-full border px-4 py-3 text-base font-normal transition-colors ${open ? 'border-neutrals-0' : ''} `}
          >
            <span className="truncate">{label}</span>
            <Icon width={16} height={16} className="shrink-0" />
          </Popover.Button>
          <Popover.Panel
            anchor={{ to: 'bottom start', gap: 4 }}
            className="rounded-custom border-neutrals-100 bg-neutrals-0 text-text-neutrals-900 shadow-light-s z-50 max-h-64! [width:var(--button-width)] overflow-y-auto border p-1"
          >
            <ul className="divide-neutrals-50 divide-y">
              <li>
                <button
                  type="button"
                  onClick={() => {
                    onSelect(undefined);
                    close();
                  }}
                  className={oneLine`flex w-full items-center justify-between gap-4 py-3 pr-2 pl-3 text-left ${!selected ? 'text-text-neutrals-900 font-bold' : 'text-text-neutrals-500'} `}
                >
                  {clearLabel}
                </button>
              </li>
              {options.map((opt) => (
                <li key={opt.value}>
                  <button
                    type="button"
                    onClick={() => {
                      onSelect(opt.value);
                      close();
                    }}
                    className={oneLine`flex w-full items-center justify-between gap-4 py-3 pr-2 pl-3 text-left ${selected === opt.value ? 'text-text-neutrals-900 font-bold' : 'text-text-neutrals-500'} `}
                  >
                    <span>{opt.label}</span>
                  </button>
                </li>
              ))}
            </ul>
          </Popover.Panel>
        </>
      );
    }}
  </Popover>
);

/**
 * Variante de LocalDropdown que carga sus opciones del índice de **flats** vía
 * `useMenu` (facet del atributo configurado). Debe vivir dentro de un
 * `<Index indexName={FLATS_INDEX_NAME}>` para que el hook resuelva al índice
 * correcto. La selección NO refina Algolia (no llamamos `refine`); la
 * guardamos en estado del padre y viaja como query param al listado.
 *
 * Así, cuando aparezcan nuevos valores del facet (ej. más tipos de vivienda
 * o más habitaciones), los dropdowns se actualizan solos — sin tocar código.
 */
const FacetDropdown: FunctionComponent<{
  attribute: string;
  label: string;
  selected?: string;
  onSelect: (value: string | undefined) => void;
  formatOption?: (value: string) => string;
  sortItems?: (a: { value: string }, b: { value: string }) => number;
  clearLabel?: string;
  sizeClass?: string;
}> = ({
  attribute,
  label,
  selected,
  onSelect,
  formatOption = (v) => v,
  sortItems,
  clearLabel,
  sizeClass,
}) => {
  const { items } = useMenu({ attribute, limit: 50 });
  const sorted = sortItems ? [...items].sort(sortItems) : items;
  const options = sorted.map((item) => ({
    value: item.value,
    label: formatOption(item.label),
  }));
  return (
    <LocalDropdown
      label={label}
      options={options}
      selected={selected}
      onSelect={onSelect}
      clearLabel={clearLabel}
      sizeClass={sizeClass}
    />
  );
};

// Ancho de los filtros de la fila oculta (Baños, Tamaño, Características). Fijo
// en desktop para no estirarlos a tercios y dejarlos alineados con la primera
// fila (Figma); ancho completo en mobile, donde la fila se apila.
const HIDDEN_FILTER_SIZE = 'w-full md:w-[180px] md:flex-none';

// Buckets de "Tamaño mín." (m² computables) — mismos cortes que el listado
// (`ListOfFlats.M2_MIN_OPTIONS`).
const SIZE_MIN_OPTIONS = [40, 50, 60, 70, 80, 90, 100, 120, 150];

// Entradas de `caracteristicasFacet.description` internas de SAP que no son
// características de cara al usuario (igual que en el listado).
const CARACTERISTICAS_OCULTAS = ['Metros Cuadrados Computables'];

/** Nombre legible de una zona ("Madrid › Cañaveral") para el input. */
const zoneDisplayName = (name: string) =>
  (name ?? '')
    .split(' > ')
    .map((seg) => {
      const l = seg.trim().toLowerCase();
      return l.charAt(0).toUpperCase() + l.slice(1);
    })
    .join(' › ');

/**
 * Dropdown multi-select sobre un facet de flats (useRefinementList). Como los
 * demás del hero NO refina Algolia: guarda la selección en el padre y viaja
 * como `?caracteristicas=` (coma-separado) al listado. Debe vivir dentro de un
 * `<Index indexName={FLATS_INDEX_NAME}>`.
 */
const MultiFacetDropdown: FunctionComponent<{
  attribute: string;
  label: string;
  selected: string[];
  onChange: (values: string[]) => void;
  hideValues?: string[];
  formatOption?: (value: string) => string;
  sizeClass?: string;
}> = ({
  attribute,
  label,
  selected,
  onChange,
  hideValues = [],
  formatOption = (v) => v,
  sizeClass = 'min-w-[180px] flex-1',
}) => {
  const { items } = useRefinementList({
    attribute,
    operator: 'and',
    limit: 50,
    sortBy: ['name:asc'],
  });
  const options = items.filter((it) => !hideValues.includes(it.value));
  const toggle = (value: string) =>
    onChange(
      selected.includes(value)
        ? selected.filter((v) => v !== value)
        : [...selected, value],
    );

  return (
    <Popover
      as="div"
      className={oneLine`text-body text-body-s relative ${sizeClass}`}
    >
      {({ open }) => {
        const Icon = open ? ChevronUp : ChevronDown;
        return (
          <>
            <Popover.Button
              className={oneLine`border-input-border bg-input-bg text-neutrals-0 hover:bg-input-bg/90 inline-flex w-full items-center justify-between gap-2 rounded-full border px-4 py-3 text-base font-normal transition-colors ${open ? 'border-neutrals-0' : ''} `}
            >
              <span className="truncate">
                {selected.length > 0 ? `${label} (${selected.length})` : label}
              </span>
              <Icon width={16} height={16} className="shrink-0" />
            </Popover.Button>
            <Popover.Panel
              anchor={{ to: 'bottom start', gap: 4 }}
              className="rounded-custom border-neutrals-100 bg-neutrals-0 text-text-neutrals-900 shadow-light-s z-50 max-h-64! [width:var(--button-width)] overflow-y-auto border p-1"
            >
              <ul className="divide-neutrals-50 divide-y">
                {options.map((opt) => (
                  <li key={opt.value}>
                    <label className="flex w-full cursor-pointer items-center gap-2 py-3 pr-2 pl-3 text-left">
                      <input
                        type="checkbox"
                        checked={selected.includes(opt.value)}
                        onChange={() => toggle(opt.value)}
                        className="border-neutrals-500 checked:text-primary-500 h-4 w-4 cursor-pointer rounded-[2px] checked:ring-0 focus:ring-0 focus:ring-offset-0 focus-visible:outline-hidden"
                      />
                      <span className="truncate">
                        {formatOption(opt.label)}
                      </span>
                    </label>
                  </li>
                ))}
              </ul>
            </Popover.Panel>
          </>
        );
      }}
    </Popover>
  );
};

/**
 * Buscador del hero centered (Figma HoM). El `<InstantSearch>` que envuelve
 * este componente está conectado al índice de **zonas** (autocompletado de
 * localización vía `useSearchBox` + `useHits`). Para Vivienda y Habitaciones
 * abrimos un `<Index>` secundario al índice de **flats** y leemos los facets
 * con `useMenu` — así las opciones crecen solas con los datos. Precio Máx no
 * es un facet (es rango numérico), va con buckets estáticos. Todas las
 * selecciones viajan al listado como query params y `ListOfFlats` las aplica.
 */
export const SearchBoxHero: FunctionComponent<TypeSearchBoxHeroProps> = ({
  className = '',
  locationPlaceholder = 'Escribe dónde buscas vivir',
  typeFlat,
}: TypeSearchBoxHeroProps) => {
  const router = useRouter();
  // Prioridad: Strapi TipoVivienda (ya en formato `ventaAlquiler`) →
  // env var (en formato `SearchTypeRoutes`, traducido) → MED_EST como default
  // razonable (= /alquiler-temporal).
  const envType = process.env.NEXT_PUBLIC_MAIN_SEARCH_TYPE as
    | SearchTypeRoutes
    | undefined;
  const ventaAlquiler: TypeFlat['ventaAlquiler'] =
    (typeFlat as TypeFlat['ventaAlquiler']) ??
    (envType && ROUTE_TO_VENTA[envType]) ??
    'MED_EST';
  const routeKey: SearchTypeRoutes = VENTA_TO_ROUTE[ventaAlquiler];
  const isRent = ventaAlquiler !== 'VENTA';
  // Reusamos los buckets del listado (`ListOfFlats`) para que el dropdown
  // del hero ofrezca exactamente los mismos cortes que los selects de min/max
  // de la página de resultados — una sola fuente de verdad en `algolia/utils`.
  const priceBuckets = priceFilterOptions(ventaAlquiler);

  const { refine } = useSearchBox({ queryHook: useAlgoliaDebounce });
  const { hits: unsortedHits } = useHits<TypeHitZone>();
  const [hits, setHits] = useState(unsortedHits);
  const [focused, setFocused] = useState(false);
  const [location, setLocation] = useState('');
  const [vivienda, setVivienda] = useState<string | undefined>(undefined);
  const [maxPrice, setMaxPrice] = useState<number | undefined>(undefined);
  const [habitaciones, setHabitaciones] = useState<number | undefined>(
    undefined,
  );
  const [banos, setBanos] = useState<string | undefined>(undefined);
  const [tamano, setTamano] = useState<number | undefined>(undefined);
  const [caracteristicas, setCaracteristicas] = useState<string[]>([]);
  // Zona elegida en las sugerencias: su path es el DESTINO al pulsar Buscar.
  // Se limpia al reescribir el input (la selección deja de ser válida).
  const [selectedZone, setSelectedZone] = useState<string | undefined>(
    undefined,
  );
  const [showMore, setShowMore] = useState(false);
  const labelRef = useRef<HTMLLabelElement>(null);

  useEffect(() => {
    setHits(sortZonasResults(unsortedHits));
  }, [unsortedHits]);

  // Cerrar sugerencias al click fuera del input.
  useEffect(() => {
    const onClick = (event: globalThis.MouseEvent) => {
      if (
        labelRef.current &&
        !labelRef.current.contains(event.target as Node)
      ) {
        setFocused(false);
      }
    };
    document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
  }, []);

  const indexCandidate = routes.flats[routeKey];
  const indexRoute =
    typeof indexCandidate === 'string'
      ? indexCandidate
      : (indexCandidate?.index ?? routes.index);

  // Slugs "marketing-friendly" que consume el routing de `ListOfFlats`
  // (ver `@/components/algolia/flats-routing`). Deben coincidir para que la
  // selección del hero se aplique al aterrizar en el listado.
  const buildQuery = () => {
    const params: Record<string, string> = {};
    // Sólo texto libre si NO se eligió una zona concreta (esa va en el path).
    if (location && !selectedZone) params.q = location;
    if (vivienda) params.tipo = vivienda;
    if (maxPrice) params['precio-max'] = String(maxPrice);
    if (habitaciones) params.dormitorios = String(habitaciones);
    if (banos) params.banos = banos;
    if (tamano) params.metros = String(tamano);
    if (caracteristicas.length) {
      params.caracteristicas = caracteristicas.join(',');
    }
    return params;
  };

  // Navegar SÓLO al pulsar Buscar (o Enter): al destino de la zona elegida si
  // la hay, o al listado general. Las selecciones de filtros no navegan.
  const navigateToListings = () => {
    router?.push({ pathname: selectedZone ?? indexRoute, query: buildQuery() });
  };

  /**
   * El CTA se pinta en dos sitios distintos según el tamaño de pantalla.
   *
   * En desktop cierra la primera fila, junto a los desplegables. En mobile todo
   * se apila en una columna y el botón tiene que quedar el ÚLTIMO, después de
   * "Más filtros" y de los filtros que despliega: si va antes, el usuario lo
   * pulsa sin haber visto que existen más filtros.
   *
   * No se puede resolver con `order` porque los dos bloques son contenedores
   * flex distintos y el botón tendría que saltar de uno a otro. Se renderiza
   * dos veces y se muestra sólo el que toca — misma función, mismo estilo.
   */
  const botonBuscar = (displayClass: string) => (
    <button
      type="button"
      onClick={navigateToListings}
      // Sin flex-1 a propósito: el CTA ciñe a su contenido y son los
      // dropdowns quienes absorben el espacio sobrante. Con flex-1 el
      // min-w pisaba el suelo automático de contenido y el botón podía
      // encogerse por debajo del texto, desbordando el icono.
      className={oneLine`text-body-l bg-secondary-500 text-neutrals-0 ${displayClass} min-w-[180px] shrink-0 items-center justify-between gap-2 rounded-2xl px-6 py-3 font-bold whitespace-nowrap transition-colors hover:bg-[rgb(var(--tw-color-button-bg-hover,var(--tw-color-secondary-400)))]`}
    >
      <span>Buscar propiedades</span>
      <HomMapPin width={24} height={24} className="shrink-0" />
    </button>
  );

  return (
    <div
      className={oneLine`bg-primary-50/70 flex w-full flex-col gap-4 rounded-2xl px-6 py-6 shadow-lg md:px-10 ${className} `}
    >
      <div className="flex flex-col flex-wrap items-stretch gap-3 md:flex-row md:items-center">
        <Index indexName={FLATS_INDEX_NAME}>
          <FacetDropdown
            attribute="tipoDeUsoDescription"
            label={vivienda ?? 'Vivienda'}
            selected={vivienda}
            onSelect={setVivienda}
          />
        </Index>

        {/* Localización: input cableado a useSearchBox sobre el índice de
            zonas + dropdown de sugerencias (useHits). Click en una sugerencia
            navega directo al listado de esa zona vía zonePath. */}
        <label
          ref={labelRef}
          htmlFor="hero-location-input"
          className="border-input-border bg-input-bg text-neutrals-0 relative flex min-w-[220px] flex-[2] items-center gap-2 rounded-full border px-4 py-3"
        >
          <MapPin width={16} height={16} className="shrink-0" />
          <input
            id="hero-location-input"
            value={location}
            onChange={(event) => {
              setLocation(event.target.value);
              // Reescribir invalida la zona previamente elegida.
              setSelectedZone(undefined);
              refine(event.target.value);
            }}
            onFocus={() => setFocused(true)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                navigateToListings();
              }
            }}
            placeholder={locationPlaceholder}
            autoComplete="off"
            className="text-neutrals-0 placeholder:text-neutrals-0 w-full border-0 bg-transparent p-0 text-base focus:border-0 focus:ring-0 focus:outline-hidden"
          />

          {focused && hits.length > 0 && (
            <div className="rounded-custom border-neutrals-100 bg-neutrals-0 shadow-light-s absolute top-full right-0 left-0 z-20 mt-1 max-h-72 overflow-y-auto border p-1">
              <ul className="divide-neutrals-50 divide-y">
                {hits.slice(0, 6).map((hit, index) => {
                  // `zonePath` ya devuelve la ruta con leading slash
                  // (vía `routes.flats[*].zone`); no añadir otro.
                  const href = zonePath(hit, ventaAlquiler);
                  return (
                    <li key={hit.objectID ?? index}>
                      <Link
                        href={href}
                        prefetch={false}
                        onClick={(event: MouseEvent<HTMLAnchorElement>) => {
                          // Seleccionar una zona rellena el input y guarda el
                          // destino; NO navega. La navegación ocurre sólo al
                          // pulsar "Buscar propiedades" (o Enter). El
                          // click-medio sigue abriendo la zona en pestaña
                          // nueva vía href.
                          event.preventDefault();
                          setLocation(zoneDisplayName(hit.name ?? ''));
                          setSelectedZone(href);
                          setFocused(false);
                        }}
                        className="hover:bg-neutrals-50 block px-3 py-3 text-sm"
                      >
                        <HighlightHitName
                          name={hit.name ?? ''}
                          query={location}
                        />
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </label>

        <LocalDropdown
          label={
            maxPrice ? `Hasta ${formatPrice(maxPrice, isRent)}` : 'Precio Máx.'
          }
          options={priceBuckets.map((p) => ({
            value: String(p),
            label: formatPrice(p, isRent),
          }))}
          selected={maxPrice ? String(maxPrice) : undefined}
          onSelect={(v) => setMaxPrice(v ? Number(v) : undefined)}
          clearLabel="Sin límite"
        />

        <Index indexName={FLATS_INDEX_NAME}>
          <FacetDropdown
            attribute="habitaciones"
            label={habitaciones ? `${habitaciones} hab.` : 'Num. Habitaciones'}
            selected={habitaciones ? String(habitaciones) : undefined}
            onSelect={(v) => setHabitaciones(v ? Number(v) : undefined)}
            formatOption={(v) =>
              `${v} ${Number(v) === 1 ? 'habitación' : 'habitaciones'}`
            }
            sortItems={(a, b) => Number(a.value) - Number(b.value)}
          />
        </Index>

        {botonBuscar('hidden md:inline-flex')}
      </div>

      <div className="flex flex-col flex-wrap items-stretch gap-3 md:flex-row md:items-center">
        <button
          type="button"
          // Toggle: muestra/oculta la segunda fila de filtros (Baños, Tamaño,
          // Características). Ya no navega — eso es exclusivo de "Buscar".
          onClick={() => setShowMore((s) => !s)}
          aria-expanded={showMore}
          className="border-input-border text-neutrals-0 hover:bg-neutrals-0/10 inline-flex w-max shrink-0 items-center gap-2 rounded-2xl border px-4 py-2 text-sm font-normal transition-colors"
        >
          <HomFilters width={16} height={16} />
          <span>{showMore ? 'Menos filtros' : 'Más filtros'}</span>
        </button>

        {showMore && (
          // Ancho fijo (Figma): en desktop los filtros ocultos no se estiran a
          // tercios; quedan a ~200px alineados con la primera fila. En mobile
          // siguen a ancho completo (fila apilada).
          <Index indexName={FLATS_INDEX_NAME}>
            <FacetDropdown
              attribute="banos"
              label={
                banos ? `${banos} ${banos === '1' ? 'baño' : 'baños'}` : 'Baños'
              }
              selected={banos}
              onSelect={setBanos}
              formatOption={(v) => `${v} ${Number(v) === 1 ? 'baño' : 'baños'}`}
              sortItems={(a, b) => Number(a.value) - Number(b.value)}
              sizeClass={HIDDEN_FILTER_SIZE}
            />
            <LocalDropdown
              label={tamano ? `Desde ${tamano} m²` : 'Tamaño mín.'}
              options={SIZE_MIN_OPTIONS.map((s) => ({
                value: String(s),
                label: `Desde ${s} m²`,
              }))}
              selected={tamano ? String(tamano) : undefined}
              onSelect={(v) => setTamano(v ? Number(v) : undefined)}
              clearLabel="Sin mínimo"
              sizeClass={HIDDEN_FILTER_SIZE}
            />
            <MultiFacetDropdown
              attribute="caracteristicasFacet.description"
              label="Características"
              selected={caracteristicas}
              onChange={setCaracteristicas}
              hideValues={CARACTERISTICAS_OCULTAS}
              sizeClass={HIDDEN_FILTER_SIZE}
            />
          </Index>
        )}
      </div>

      {/* Sólo mobile: cierra la columna, después de los filtros. */}
      {botonBuscar('inline-flex md:hidden')}
    </div>
  );
};

SearchBoxHero.displayName = 'SearchBoxHero';
